import React, { useMemo, useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useAuthModal } from "../../App";
import { useGameSession, useMidnightCountdown, MAX_ATTEMPTS } from "../../hooks/useGameSession";
import { useNextChallenge } from "../../hooks/useNextChallenge";
import ResultModal from "../../components/ResultModal";
import AchievementPopup from "../../components/AchievementPopup";
import AttemptRow from "../../components/AttemptRow";
import ConfirmDialog from "../../components/ConfirmDialog";
import "../../styles/GuessLorePage.css";

export default function GuessLorePage() {
  const navigate  = useNavigate();
  const { open: openAuth } = useAuthModal();
  const countdown = useMidnightCountdown();

  const s = useGameSession({
    gameName:       "lore",
    itemIdKey:      "name",
    correctNameKey: "correct_lore_name",
    dailyKey:       "daily_lore",
  });

  // ── Next Challenge tracking ──
  const { markCompleted, nextChallenge, allCompleted } = useNextChallenge("lore", s.gameMode);

  React.useEffect(() => {
    if (s.game.finished) markCompleted();
  }, [s.game.finished, markCompleted]);

  const hints      = s.game.hints || {};
  const eliminated = hints.eliminated || [];

  const question = s.dailyItem?.question || null;

  // Shuffle all 6 options ONCE when the question loads, then keep order stable.
  // Eliminated options stay in place (greyed out) so the correct answer
  // position doesn't become obvious by process of elimination.
  const allOptions     = hints.options ? [...hints.options, ...eliminated] : [];
  const questionId     = s.dailyItem?.question_id ?? null;
  const shuffledRef    = useRef([]);
  const lastQuestionId = useRef(null);

  if (questionId !== lastQuestionId.current && allOptions.length > 0) {
    // New question loaded — shuffle once and lock order
    const shuffled = [...allOptions].sort(() => Math.random() - 0.5);
    shuffledRef.current  = shuffled;
    lastQuestionId.current = questionId;
  }

  const stableOrder = shuffledRef.current.length > 0 ? shuffledRef.current : allOptions;

  return (
    <div className="gs-root">
      <Helmet>
        <title>Azerodle | Answer Lore Question</title>
        <meta name="description" content="Test your World of Warcraft lore knowledge. A new daily question from the history of Azeroth." />
      </Helmet>
      <div className="gs-wrapper">

        {s.isGuest && (
          <div className="gs-guest-banner">
            <span>Playing as <strong>guest</strong> — rewards and statistics unavailable</span>
            <button className="gs-ghost-btn" onClick={openAuth}>Sign up</button>
          </div>
        )}

        <div className="gs-page-title">
          <h1 className="gs-title">Answer Lore Question</h1>
        </div>

        {s.initialLoading ? (
          <div className="gs-panel gs-panel--ready">
            <div className="gs-panel-spinner"><div className="gs-spinner" /></div>
          </div>
        ) : (
          <div className="gs-panel gs-panel--ready">
            <div className="gs-panel-content">

              <div className="gs-mode-row">
                {["classic", "retail"].map(mode => (
                  <button
                    key={mode}
                    className={"gs-mode-btn " + (s.gameMode === mode ? "active" : "")}
                    onClick={() => s.switchMode(mode)}
                  >
                    {mode === "retail" ? "Retail" : "Classic"}
                  </button>
                ))}
              </div>

              <div className="gs-divider" />
              <div className={["gs-panel-body", s.showBody ? "gs-panel-body--ready" : "", s.fading ? "gs-panel-body--fading" : ""].join(" ")}>
              {s.contentReady && (<>

                {!s.game.finished && (<>

                  <div className="gl-question-wrap">
                    <div className="gl-question-card">
                      <p className="gl-question-text">{question || "Loading question…"}</p>
                    </div>
                  </div>

                  <div className="gs-progress-wrap">
                    <div className="gs-progress-meta">
                      <span className="gs-progress-label">Attempts</span>
                      <span className={"gs-progress-count " + (s.attemptsUsed >= MAX_ATTEMPTS ? "exhausted" : "")}>
                        {s.attemptsUsed} / {MAX_ATTEMPTS}
                      </span>
                    </div>
                    <div className="gs-progress-track">
                      <div
                        className={"gs-progress-fill " + (s.game.shouldAnimateProgress ? "animate" : "")}
                        style={{ width: s.game.progressWidth + "%" }}
                      />
                    </div>
                  </div>
                </>)}

                {s.game.finished ? (
                  <div className={"gs-finished " + (s.didWin ? "win" : "lose")}>
                    <div className="gs-finished-icon">{s.didWin ? "🏆" : "💀"}</div>
                    <p className="gs-finished-title">{s.didWin ? "Correct!" : "Game Over"}</p>
                    <p className="gs-finished-sub">
                      {s.didWin
                        ? <>You answered <strong>{s.correctAnswer?.user_answer}</strong> in{" "}
                            <strong>{s.correctAnswer?.attempt_number}</strong>{" "}
                            {s.correctAnswer?.attempt_number === 1 ? "attempt" : "attempts"}</>
                        : <>The correct answer was <strong>{s.game.correctAnswerName || "???"}</strong></>
                      }
                    </p>
                    <div className="gs-finished-countdown">
                      <span className="gs-finished-countdown-label">Next question in</span>
                      <span className="gs-finished-countdown-time">{countdown}</span>
                    </div>

                    {/* Next Challenge button */}
                    {nextChallenge ? (
                      <button
                        className="gs-btn-next-challenge"
                        onClick={() => {
                          if (nextChallenge.game === "lore") {
                            s.switchMode(nextChallenge.mode);
                          } else {
                            navigate(nextChallenge.path + "?mode=" + nextChallenge.mode);
                          }
                        }}
                      >
                        <span className="gs-btn-next-challenge-text">Next Challenge</span>
                      </button>
                    ) : allCompleted ? (
                      <div className="gs-all-done">
                        <span className="gs-all-done-text">All daily challenges completed!</span>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="gs-input-section">

                    {/* Option buttons — stable order, eliminated stay in place */}
                    <div className="gl-option-buttons">
                      {stableOrder.map(opt => {
                        const isEliminated = eliminated.includes(opt);
                        const isSelected   = s.selectedItem?.name === opt;
                        return (
                          <button
                            key={opt}
                            className={[
                              "gl-option-btn",
                              isEliminated ? "gl-option-btn--eliminated" : "",
                              isSelected   ? "gl-option-btn--selected"   : "",
                            ].join(" ").trim()}
                            onClick={() => !isEliminated && s.handleSelect({ name: opt })}
                            disabled={isEliminated || s.submitting}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      className={"gs-btn-gold " + (s.submitting ? "submitting" : "")}
                      onClick={s.makeGuess}
                      disabled={!s.selectedItem || s.submitting}
                    >
                      {s.submitting ? "Checking…" : "Submit Answer"}
                    </button>

                    <div className="gs-secondary-actions">
                      <button
                        className="gs-btn-secondary gs-btn-skip"
                        onClick={() => s.setConfirmAction("skip")}
                        disabled={s.submitting}
                      >
                        Skip Attempt
                      </button>
                      <button
                        className="gs-btn-secondary gs-btn-surrender"
                        onClick={() => s.setConfirmAction("surrender")}
                        disabled={s.submitting}
                      >
                        Surrender
                      </button>
                    </div>

                    {s.error && <p className="gs-error">⚠ {s.error}</p>}
                  </div>
                )}

                {!s.game.finished && (<>
                  <div className="gs-divider" />
                  <div className="gs-history">
                    <p className="gs-history-label">Attempt History</p>
                    {Array.from({ length: MAX_ATTEMPTS }, (_, i) => {
                      const attempt = s.game.answers[i];
                      return attempt
                        ? <AttemptRow
                            key={s.gameMode + "-" + attempt.attempt_number}
                            attempt={attempt}
                            index={i}
                            animate={s.game.animateAttempts && i === s.game.answers.length - 1}
                          />
                        : <div key={i} className="gs-attempt-row gs-attempt-empty">
                            <span className="gs-attempt-num">#{i + 1}</span>
                            <span className="gs-attempt-empty-text">—</span>
                          </div>;
                    })}
                  </div>
                </>)}

              </>)}
              </div>
            </div>
          </div>
        )}
      </div>

      {s.confirmAction && (
        <ConfirmDialog
          type={s.confirmAction}
          onConfirm={s.handleConfirm}
          onCancel={s.handleCancelConfirm}
        />
      )}

      {s.showModal && (
        <ResultModal
          result={s.lastResult}
          onClose={s.handleResultClose}
          isGuest={s.isGuest}
          onRegister={openAuth}
          itemName={s.correctAnswer?.user_answer || s.game.correctAnswerName}
          itemLabel="answer"
          gameName="lore"
          gameMode={s.gameMode}
          answers={s.game.answers}
          stats={s.stats}
          xpResult={s.isGuest ? null : s.xpResult}
        />
      )}

      {s.showAchievement && s.achievementQueue.length > 0 && (
        <AchievementPopup
          achievement={s.achievementQueue[0]}
          onClose={s.handleAchievementClose}
        />
      )}
    </div>
  );
}